import React, { useState, useEffect } from 'react';
import { SHeaderSortable, SPagination } from '@avalara/skylab-sdk/react';
import _ from 'lodash';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';
import CompaniesFilterPanel from './CompaniesFilterPanel';
import NoData from '../page/NoData';

const ROWS_PER_PAGE = 10;

const ActionIconButton = ({ iconName, iconClass, ariaLabel, onClick, testId, excludeFromReadonly }) => (
    <button
        type="button"
        className="ghost companies-table-action-button"
        onClick={onClick}
        aria-label={ariaLabel}
        title={ariaLabel}
        data-testid={testId}
        {...(excludeFromReadonly ? { excludeFromReadonly: true } : {})}
    >
        <s-icon name={iconName} class={iconClass} role="img" aria-label={ariaLabel} />
    </button>
);


const VatNumberActionsCell = ({ company, languages, onOpenVATNumbers, onOpenAddVATNumbers }) => (
    <ReadOnlyWrapper>
        <div className="companies-table-actions">
            <ActionIconButton
                iconName="eye-on"
                iconClass="blue-dark"
                ariaLabel={LANGUAGES_CONTROLLER.format(languages, 'companiesPage_viewCompanyVATNumbers')}
                onClick={() => onOpenVATNumbers(company)}
                testId="row-view-vat-numbers"
                excludeFromReadonly={true}
            />
            <ActionIconButton
                iconName="plus"
                iconClass="blue-dark"
                ariaLabel={LANGUAGES_CONTROLLER.format(languages, 'companiesPage_addVATNumber')}
                onClick={() => onOpenAddVATNumbers(company)}
                testId="row-add-vat-number"
            />
        </div>
    </ReadOnlyWrapper>
);


const CompanyActionsCell = ({ company, languages, onOpenEditCompany, onDeleteCompany }) => (
    <ReadOnlyWrapper>
        <div className="companies-table-actions">
            <ActionIconButton
                iconName="edit-pencil"
                iconClass="blue-dark"
                ariaLabel={LANGUAGES_CONTROLLER.format(languages, 'companiesPage_editCompanyDetails')}
                onClick={() => onOpenEditCompany(company)}
                testId="row-edit-company"
                excludeFromReadonly={true}
            />
            <ActionIconButton
                iconName="trash"
                iconClass="warning"
                ariaLabel={LANGUAGES_CONTROLLER.format(languages, 'companiesPage_deleteCompany')}
                onClick={() => onDeleteCompany(company.id)}
                testId="row-delete-company"
            />
        </div>
    </ReadOnlyWrapper>
);


const CompaniesTable = (props) => {
    const [sortingConfig, setSortingConfig] = useState({ columnId: 'name', order: 'asc' });
    const [pagingConfig, setPagingConfig] = useState({ startIndex: 0, endIndex: ROWS_PER_PAGE - 1, rowsPerPage: ROWS_PER_PAGE });
    const [filteringConfig, setFilteringConfig] = useState({ name: '', code: '', vatNumber: '' });
    const [renderedCompaniesCount, setRenderedCompaniesCount] = useState(props.companies?.length || 0);

    const onSort = (e) => {
        const columnId = e.detail.id;
        const order = e.detail.ascending ? 'asc' : 'desc';
        if (['name', 'code'].includes(columnId)) {
            setSortingConfig({ columnId, order });
        }
    };

    const onPaginate = (e) => {
        setPagingConfig({
            startIndex: e.detail.startIndex,
            endIndex: e.detail.endIndex,
            rowsPerPage: e.detail.rowsPerPage
        });
    };

    const getFilteredCompanies = () => {
        if (!props.companies?.length) {
            return [];
        }

        return props.companies.filter((company) => {
            if (filteringConfig.name) {
                const search = filteringConfig.name.toLowerCase();
                if (!company.name?.toLowerCase().includes(search)) {
                    return false;
                }
            }
            if (filteringConfig.code) {
                const search = filteringConfig.code.toLowerCase();
                if (!company.code?.toLowerCase().includes(search)) {
                    return false;
                }
            }
            if (filteringConfig.vatNumber) {
                const search = filteringConfig.vatNumber.toLowerCase();
                const vatNumbers = company.vatNumbers || [];
                if (!vatNumbers.some((v) => v.vatnumber?.toLowerCase().includes(search))) {
                    return false;
                }
            }
            return true;
        });
    };

    const generateSortedPage = (companies) => {
        if (!companies?.length) {
            return null;
        }

        const sortedCompanies = sortingConfig.columnId
            ? _.orderBy(companies, [sortingConfig.columnId], [sortingConfig.order])
            : companies;

        const currentPage = sortedCompanies.slice(pagingConfig.startIndex, pagingConfig.endIndex + 1);

        return currentPage.map((company) => (
            <tr key={company.id}>
                <td>{company.name}</td>
                <td>{company.code}</td>
                <td className="companies-table-action-column">
                    <VatNumberActionsCell
                        company={company}
                        languages={props.languages}
                        onOpenVATNumbers={props.onOpenVATNumbers}
                        onOpenAddVATNumbers={props.onOpenAddVATNumbers}
                    />
                </td>
                <td className="companies-table-action-column">
                    <CompanyActionsCell
                        company={company}
                        languages={props.languages}
                        onOpenEditCompany={props.onOpenEditCompany}
                        onDeleteCompany={props.onDeleteCompany}
                    />
                </td>
            </tr>
        ));
    };

    const filteredCompanies = getFilteredCompanies();
    const companyRows = generateSortedPage(filteredCompanies);
    const hasActiveFilter = Boolean(filteringConfig.name || filteringConfig.code || filteringConfig.vatNumber);
    const showNoFilterResults = hasActiveFilter && filteredCompanies.length === 0;

    useEffect(() => {
        if (filteredCompanies.length !== renderedCompaniesCount) {
            setPagingConfig((prev) => {
                const rowsPerPage = prev.rowsPerPage;
                const endIndex = Math.max(Math.min(rowsPerPage, filteredCompanies.length) - 1, 0);
                return {
                    startIndex: 0,
                    endIndex,
                    rowsPerPage
                };
            });
            setRenderedCompaniesCount(filteredCompanies.length);
        }
    }, [filteredCompanies.length, renderedCompaniesCount]);

    return (
        <div className="mb20">
            <div className="companies-filter-panel-wrapper">
                <CompaniesFilterPanel
                    open={props.toggleFilterPanel}
                    shouldRestoreOpen={props.shouldRestoreFilterPanelOpen}
                    filteringConfig={filteringConfig}
                    onDismiss={props.onFilterPanelDismiss}
                    setFilteringConfig={setFilteringConfig}
                />
            </div>
            <s-table-container data-testid="companies-table" tabindex="0">
                <table className="companies-table">
                    <colgroup>
                        <col className="companies-table-name-col" />
                        <col className="companies-table-code-col" />
                        <col className="companies-table-vat-actions-col" />
                        <col className="companies-table-company-actions-col" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th data-testid="companies-name-column" className="pb0 pt4">
                                <SHeaderSortable
                                    id="name"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, 'companiesPage_Name')}
                                    onS-sort={onSort}
                                />
                            </th>
                            <th data-testid="companies-code-column" className="pb0 pt4">
                                <SHeaderSortable
                                    id="code"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, 'companiesPage_CompanyCode')}
                                    onS-sort={onSort}
                                />
                            </th>
                            <th data-testid="companies-vat-number-column" className="pb0 pt4 companies-table-action-column">
                                {LANGUAGES_CONTROLLER.format(props.languages, 'companiesPage_VATNumberActions')}
                            </th>
                            <th data-testid="companies-company-actions-column" className="pb0 pt4 companies-table-action-column">
                                {LANGUAGES_CONTROLLER.format(props.languages, 'companiesPage_CompanyActions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {showNoFilterResults ? (
                            <tr data-testid="companies-no-data-row">
                                <td colSpan="4">
                                    <div className="companies-table-no-data" data-testid="companies-no-data">
                                        <NoData noDataText="companies_NoData" />
                                    </div>
                                </td>
                            </tr>
                        ) : companyRows}
                    </tbody>
                </table>
            </s-table-container>
            <SPagination
                className="mt20 mb40"
                totalRecords={renderedCompaniesCount}
                startIndex={pagingConfig.startIndex}
                rowsPerPage={pagingConfig.rowsPerPage}
                onS-paginate={onPaginate}
            />
        </div>
    );
};


export default CompaniesTable;
